import { useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Slider,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import RevealIcon from "@mui/icons-material/VisibilityOutlined"
import HideIcon from "@mui/icons-material/VisibilityOffOutlined"
import DiceIcon from "@mui/icons-material/CasinoOutlined"

import { vaultApi } from "../../api"
import PasswordStrength from "../../components/PasswordStrength"

/**
 * Adding or changing one entry.
 *
 * ── Why the generator is here and not a separate tool ───────────────────────
 *
 * A generator somewhere else is a generator people do not use: the moment a
 * password is needed is the moment this dialog is open, and one button beats
 * opening another tab and trusting whatever it produces.
 *
 * ── Why it is generated in the browser ──────────────────────────────────────
 *
 * `crypto.getRandomValues`, not `Math.random`, and locally rather than through
 * the API. Math.random is not unpredictable and has been the quiet hole in
 * plenty of these; a round trip would put a password on the wire twice for no
 * gain, since it has to be sent once to be stored anyway.
 *
 * ── Why editing does not show the current secret ────────────────────────────
 *
 * Opening the editor is not the same act as revealing, and it should not leave
 * the same trace or carry the same risk. The field is left empty and says what
 * empty means: leave it alone. Changing it is the deliberate act, and it is the
 * one that moves "last changed".
 */

const ALPHABET = {
  lower: "abcdefghijkmnopqrstuvwxyz",
  upper: "ABCDEFGHJKLMNPQRSTUVWXYZ",
  digits: "23456789",
  symbols: "!@#$%^&*()-_=+[]{}:,.?",
}

/** One character, drawn from `pool` with the browser's CSPRNG. */
function pick(pool) {
  const draw = new Uint32Array(1)
  crypto.getRandomValues(draw)
  /* Modulo over a 32-bit draw. The bias across a pool of ~70 is below one part
     in fifty million per character, which is not the weak link in a password a
     human will also write on a sticky note. */
  return pool[draw[0] % pool.length]
}

/**
 * A password nobody has to remember.
 *
 * ── Why one of each class is guaranteed ─────────────────────────────────────
 *
 * A uniform draw of twenty characters has about a one-in-eleven chance of
 * containing no digit at all, and the place that discovers this is the carrier
 * portal that insists on one — after the password has been saved here and the
 * account is half set up. So one character from each enabled class is placed
 * first and the rest is filled uniformly, then the whole thing is shuffled so
 * the guaranteed ones are not always at the front.
 *
 * ── Why these alphabets ─────────────────────────────────────────────────────
 *
 * The lookalikes — l, I, 1, O, 0 — are left out. Somebody reads one of these
 * out over the phone eventually, and a character that has to be spelled is a
 * character that gets typed wrong.
 */
function generate({ length, symbols }) {
  const classes = [ALPHABET.lower, ALPHABET.upper, ALPHABET.digits, ...(symbols ? [ALPHABET.symbols] : [])]
  const pool = classes.join("")
  const out = classes.slice(0, Math.min(classes.length, length)).map(pick)
  while (out.length < length) out.push(pick(pool))

  // Fisher-Yates, with the same source of randomness as the characters.
  for (let i = out.length - 1; i > 0; i -= 1) {
    const draw = new Uint32Array(1)
    crypto.getRandomValues(draw)
    const j = draw[0] % (i + 1)
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out.join("")
}

export default function VaultItemDialog({ state, clientId, onClose, onSaved }) {
  const mode = state?.mode
  const existing = state?.item

  const [name, setName] = useState("")
  const [category, setCategory] = useState("General")
  const [username, setUsername] = useState("")
  const [url, setUrl] = useState("")
  const [secret, setSecret] = useState("")
  const [notes, setNotes] = useState("")
  const [show, setShow] = useState(false)
  const [length, setLength] = useState(20)
  const [symbols, setSymbols] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!state) return
    setName(existing?.name || "")
    setCategory(existing?.category || "General")
    setUsername(existing?.username || "")
    setUrl(existing?.url || "")
    setSecret("")
    setNotes("")
    setShow(false)
    setError(null)
  }, [state, existing])

  const submit = async (event) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    const scope = clientId ? { client_id: clientId } : {}
    try {
      if (mode === "create") {
        await vaultApi.create({ ...scope, name, category, username, url, secret, notes: notes || undefined })
        onSaved("Entry added.")
      } else {
        await vaultApi.update(existing.id, {
          ...scope,
          name,
          category,
          username,
          url,
          ...(secret ? { secret } : {}),
          ...(notes ? { notes } : {}),
        })
        onSaved("Entry updated.")
      }
    } catch (err) {
      setError(err.userMessage || "That could not be saved.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={Boolean(state)} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>
          {mode === "create" ? "Add an entry" : `Edit “${existing?.name}”`}
          <Typography variant="caption" color="text.secondary" display="block">
            The password is encrypted before it is stored. Everything else on this form is not.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                label="What it is for"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Carrier portal"
                required
                fullWidth
                autoFocus
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Category"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                placeholder="Carriers"
                fullWidth
                helperText="Free text — it groups the list"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                fullWidth
                autoComplete="off"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Web address"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://portal.example.com"
                fullWidth
              />
            </Grid>

            <Grid size={12}>
              <TextField
                label="Password"
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
                type={show ? "text" : "password"}
                required={mode === "create"}
                fullWidth
                autoComplete="new-password"
                helperText={
                  mode === "create" ? "This is the part that gets encrypted." : "Leave it empty to keep the current one."
                }
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={show ? "Hide" : "Show what you are typing"}>
                          <IconButton size="small" onClick={() => setShow((current) => !current)} aria-label="Show the password">
                            {show ? <HideIcon fontSize="small" /> : <RevealIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Generate one">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSecret(generate({ length, symbols }))
                              setShow(true)
                            }}
                            aria-label="Generate a password"
                          >
                            <DiceIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                    sx: { fontFamily: show ? "monospace" : undefined },
                  },
                }}
              />
              {secret ? <PasswordStrength value={secret} /> : null}
            </Grid>

            <Grid size={12}>
              <Box sx={{ px: 1, py: 1, borderRadius: 2, bgcolor: "action.hover" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
                    Generate: {length} characters
                  </Typography>
                  <Slider
                    size="small"
                    value={length}
                    min={8}
                    max={64}
                    onChange={(_event, value) => setLength(value)}
                    sx={{ flex: 1, maxWidth: 260 }}
                    aria-label="Generated password length"
                  />
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Switch
                      size="small"
                      checked={symbols}
                      onChange={(event) => setSymbols(event.target.checked)}
                      inputProps={{ "aria-label": "Include symbols" }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Symbols
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Grid>

            <Grid size={12}>
              <TextField
                label="Notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                fullWidth
                multiline
                rows={2}
                helperText={
                  mode === "create"
                    ? "Encrypted too — the PIN, the security answers, the account number."
                    : "Encrypted too. Leave empty to keep what is there."
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving || !name.trim() || (mode === "create" && !secret)}>
            {saving ? "Saving…" : mode === "create" ? "Add it" : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
